import type {
  RemainingLimitResponseDto,
  UsageLimitSetItemDto,
} from '@shared/services/apiService/apiService';
import { SECONDS_PER_HOUR, USAGE_UNITS } from './constants';

export type CompanyLimitsUsageFields = {
  callHoursMonth: string;
  sttHoursMonth: string;
  llmTokensMonth: string;
  ttsCharactersMonth: string;
};

export type CompanyLimitsFormValues = CompanyLimitsUsageFields & {
  concurrentLimit: number | '';
};

export function getEmptyCompanyLimitsFormValues(): CompanyLimitsFormValues {
  return {
    concurrentLimit: '',
    callHoursMonth: '',
    llmTokensMonth: '',
    sttHoursMonth: '',
    ttsCharactersMonth: '',
  };
}

export function secondsToHours(seconds: number): number {
  return seconds / SECONDS_PER_HOUR;
}

export function hoursToSecondsRounded(hours: number): number {
  return Math.round(hours * SECONDS_PER_HOUR);
}

export function formatHoursFromApiLimitSeconds(limitStr: string): string {
  const sec = Number(limitStr);
  if (!Number.isFinite(sec) || sec < 0) {
    return '';
  }
  return String(secondsToHours(sec));
}

export function parsePositiveHoursInput(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  return n;
}

export function parseLlmTokensLimitInput(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  return Math.round(n);
}

export function mapRemainingLimitsToUsageFormFields(
  data: RemainingLimitResponseDto,
): CompanyLimitsUsageFields {
  const rows = data.limits ?? [];
  const callRow = rows.find((row) => row.unit === USAGE_UNITS.callSecond);
  const llmRow = rows.find((row) => row.unit === USAGE_UNITS.llmTotalTokens);
  const sttRow = rows.find((row) => row.unit === USAGE_UNITS.sttSecond);
  const ttsRow = rows.find((row) => row.unit === USAGE_UNITS.ttsCharacter);
  return {
    callHoursMonth: callRow ? formatHoursFromApiLimitSeconds(callRow.limit) : '',
    llmTokensMonth: llmRow ? String(llmRow.limit) : '',
    sttHoursMonth: sttRow ? formatHoursFromApiLimitSeconds(sttRow.limit) : '',
    ttsCharactersMonth: ttsRow ? String(ttsRow.limit) : '',
  };
}

export function mapRemainingLimitsToFormValues(
  data: RemainingLimitResponseDto,
): CompanyLimitsFormValues {
  return {
    concurrentLimit: data.concurrent?.limit != null ? Number(data.concurrent.limit) : '',
    ...mapRemainingLimitsToUsageFormFields(data),
  };
}

export function buildUsageLimitItemsFromForm(
  values: CompanyLimitsUsageFields,
): UsageLimitSetItemDto[] | null {
  const items: UsageLimitSetItemDto[] = [];

  if (values.callHoursMonth.trim() !== '') {
    const hours = parsePositiveHoursInput(values.callHoursMonth);
    if (hours === null) {
      return null;
    }
    items.push({
      unit: USAGE_UNITS.callSecond,
      limitPerMonth: String(hoursToSecondsRounded(hours)),
    });
  }

  if (values.sttHoursMonth.trim() !== '') {
    const hours = parsePositiveHoursInput(values.sttHoursMonth);
    if (hours === null) {
      return null;
    }
    items.push({
      unit: USAGE_UNITS.sttSecond,
      limitPerMonth: String(hoursToSecondsRounded(hours)),
    });
  }

  if (values.llmTokensMonth.trim() !== '') {
    const tokens = parseLlmTokensLimitInput(values.llmTokensMonth);
    if (tokens === null) {
      return null;
    }
    items.push({
      unit: USAGE_UNITS.llmTotalTokens,
      limitPerMonth: String(tokens),
    });
  }

  if (values.ttsCharactersMonth.trim() !== '') {
    const chars = parseLlmTokensLimitInput(values.ttsCharactersMonth);
    if (chars === null) {
      return null;
    }
    items.push({
      unit: USAGE_UNITS.ttsCharacter,
      limitPerMonth: String(chars),
    });
  }

  return items;
}

const UUID_IN_MESSAGE_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi;

export function formatOptionLimitsConflictMessage(
  message: string,
  voiceNameById: ReadonlyMap<string, string>,
  providerTitleById: ReadonlyMap<string, string>,
): string {
  return message.replace(UUID_IN_MESSAGE_PATTERN, (uuid) => {
    const voiceName = voiceNameById.get(uuid);
    if (voiceName) {
      return voiceName;
    }
    const providerTitle = providerTitleById.get(uuid);
    if (providerTitle) {
      return providerTitle;
    }
    return uuid;
  });
}

export function formatUsedLimitPairAsHours(usedStr: string, limitStr: string): string {
  const u = Number(usedStr);
  const l = Number(limitStr);
  if (!Number.isFinite(u) || !Number.isFinite(l)) {
    return '—';
  }
  const fmt = (seconds: number) => {
    const h = secondsToHours(seconds);
    if (Number.isInteger(h)) {
      return String(h);
    }
    return String(Math.round(h * 100) / 100);
  };
  return `${fmt(u)} / ${fmt(l)}`;
}
